import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import { loadGoogleMaps } from '@/lib/google-maps';

/**
 * Where the store sits, as a pin you can move.
 *
 * The original had this and I had replaced it with a read-only embed, which
 * turned out to need a Google API the client's key is not enabled for. This
 * uses the same Maps JavaScript API the old portal did, so the existing key
 * works unchanged.
 *
 * The pin is the input, not a picture of one: drag it, or click anywhere on
 * the map, and the latitude and longitude fields follow. Typing into those
 * fields moves the pin back.
 */

/** San Juan, so a store with no pin yet opens somewhere recognisable. */
const FALLBACK = { lat: 18.4655, lng: -66.1057 };

export function LocationMap({
  lat,
  lon,
  onChange,
}: {
  lat: string | null;
  lon: string | null;
  onChange: (position: { lat: string; lon: string }) => void;
}) {
  const { t } = useTranslation();
  const container = useRef<HTMLDivElement>(null);
  const map = useRef<google.maps.Map | null>(null);
  const marker = useRef<google.maps.Marker | null>(null);
  /** Read inside listeners, which are attached once and would capture a stale prop. */
  const report = useRef(onChange);
  report.current = onChange;

  const [state, setState] = useState<'loading' | 'ready' | 'failed'>('loading');

  const parsed = {
    lat: Number(lat),
    lng: Number(lon),
  };
  const hasPin =
    !!lat && !!lon && Number.isFinite(parsed.lat) && Number.isFinite(parsed.lng);

  // Create the map once. Position is applied by the effect below, so this does
  // not need to re-run when the coordinates change.
  useEffect(() => {
    let cancelled = false;

    loadGoogleMaps()
      .then((maps) => {
        if (cancelled || !container.current) return;

        const centre = hasPin ? parsed : FALLBACK;
        map.current = new maps.Map(container.current, {
          center: centre,
          zoom: hasPin ? 17 : 11,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        });

        marker.current = new maps.Marker({
          map: map.current,
          position: centre,
          draggable: true,
        });

        const publish = (position: google.maps.LatLng | null | undefined) => {
          if (!position) return;
          report.current({
            lat: String(position.lat()),
            lon: String(position.lng()),
          });
        };

        marker.current.addListener('dragend', () =>
          publish(marker.current?.getPosition())
        );
        // Clicking the map moves the pin, which is how a store with no
        // coordinates gets its first one without typing numbers.
        map.current.addListener('click', (event: google.maps.MapMouseEvent) => {
          marker.current?.setPosition(event.latLng);
          publish(event.latLng);
        });

        setState('ready');
      })
      .catch(() => {
        if (!cancelled) setState('failed');
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Typed coordinates move the pin. Guarded so the map does not fight the
  // dragging that produced them.
  useEffect(() => {
    if (state !== 'ready' || !hasPin || !marker.current || !map.current) return;

    const current = marker.current.getPosition();
    const moved =
      !current
      || Math.abs(current.lat() - parsed.lat) > 1e-9
      || Math.abs(current.lng() - parsed.lng) > 1e-9;

    if (moved) {
      marker.current.setPosition(parsed);
      map.current.panTo(parsed);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, hasPin, parsed.lat, parsed.lng]);

  if (state === 'failed') {
    return (
      <p className="text-muted-foreground rounded-lg border border-dashed py-10 text-center text-sm">
        {t('businessInfo.mapFailed')}
      </p>
    );
  }

  return (
    <div className="relative">
      <div ref={container} className="h-72 w-full rounded-lg border" />
      {state === 'loading' && (
        <div className="bg-muted/40 text-muted-foreground absolute inset-0 flex items-center justify-center rounded-lg border">
          <Loader2 className="size-5 animate-spin" />
        </div>
      )}
      {state === 'ready' && (
        <p className="text-muted-foreground mt-1 text-xs">
          {hasPin ? t('businessInfo.mapHint') : t('businessInfo.mapNoPinHint')}
        </p>
      )}
    </div>
  );
}
